/* Shared site-wide top nav, used by every page.
   Determine current page via data-page on <body> (or window.MM_PAGE).
   Marks the active link. Links point to real page files. */

(function () {
  const { useEffect, useState } = React;

  const BASE = window.MM_BASE || './';
  const LOGO_V = '';

  // Page order defines nav order. Home is not a top-level link — logo is home.
  const NAV_ITEMS = [
    { key: 'platform',   label: 'Platform',    href: 'platform.html' },
    { key: 'about',      label: 'About',       href: 'about.html' },
  ];

  // Mobile panel includes Home — the logo is a tap target on desktop, but
  // in a slide-down panel "go home" deserves an explicit row.
  const MOBILE_PANEL_ITEMS = [
    { key: 'home', label: 'Home', href: 'index.html' },
    ...NAV_ITEMS,
  ];

  function SharedNav({ current }) {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
      const onScroll = () => setScrolled(window.scrollY > 20);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Lock body scroll while the mobile panel is open.
    useEffect(() => {
      if (!mobileOpen) return undefined;
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }, [mobileOpen]);

    // Close on resize past the mobile breakpoint so we don't leave the
    // panel orphaned when a user rotates a tablet to landscape.
    useEffect(() => {
      const onResize = () => {
        if (window.innerWidth >= 768) setMobileOpen(false);
      };
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }, []);

    // Home link: if we're on home, scroll to top; else go to index.html
    const homeHref = current === 'home' ? '#top' : 'index.html';

    return (
      <nav
        className={`mm-nav${scrolled ? ' is-scrolled' : ''}${mobileOpen ? ' is-mobile-open' : ''}`}
      >
        <div className="mm-nav-left">
          <a className="mm-nav-logo" href={homeHref} aria-label="Movie Minder — home">
            <img
              className="mm-nav-logo-img"
              src={`${BASE}logo/movie-minder.svg${LOGO_V}`}
              alt="Movie Minder"
            />
          </a>
          <div className="mm-nav-links">
            {NAV_ITEMS.map((it) => (
              <a
                key={it.key}
                href={it.href}
                className={it.key === current ? 'is-active' : ''}
                aria-current={it.key === current ? 'page' : undefined}
              >
                {it.label}
              </a>
            ))}
          </div>
        </div>
        <div className="mm-nav-right">
          <button
            type="button"
            className="mm-btn mm-btn-primary mm-btn-sm mm-nav-cta"
            onClick={() => { window.location.href = 'contact.html'; }}
          >
            Book a demo
          </button>
          <button
            type="button"
            className="mm-nav-trigger"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mm-mobile-panel"
            onClick={() => setMobileOpen((o) => !o)}
          >
            <span className="mm-nav-trigger-bar" />
            <span className="mm-nav-trigger-bar" />
            <span className="mm-nav-trigger-bar" />
          </button>
        </div>
        <div
          id="mm-mobile-panel"
          className="mm-nav-panel"
          role="dialog"
          aria-modal={mobileOpen}
          aria-hidden={!mobileOpen}
        >
          <ul className="mm-nav-panel-list">
            {MOBILE_PANEL_ITEMS.map((it) => (
              <li key={it.key}>
                <a
                  href={it.href}
                  className={it.key === current ? 'is-active' : ''}
                  aria-current={it.key === current ? 'page' : undefined}
                  onClick={() => setMobileOpen(false)}
                >
                  {it.label}
                </a>
              </li>
            ))}
          </ul>
          <div className="mm-nav-panel-cta">
            <button
              type="button"
              className="mm-btn mm-btn-primary"
              onClick={() => { setMobileOpen(false); window.location.href = 'contact.html'; }}
            >
              Book a demo
            </button>
          </div>
        </div>
      </nav>
    );
  }

  window.SharedNav = SharedNav;
})();
